/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import { withTranslation, useTranslation } from 'react-i18next'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Container,
  Box,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Tooltip
} from '@mui/material'
import DataTable from '../components/DataTable'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import SearchBar from '../components/TableHeader/SearchBar'
import DateRangeFilter from '../components/TableHeader/DateRangeFilter'
import TableHeader from '../components/TableHeader'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableCustomStyles'
import {
  getCourierOrders,
  updateCourierOrderStatus,
  dispatchCourierOrderToRiders
} from '../apollo'

const GET_COURIER_ORDERS = gql`
  ${getCourierOrders}
`
const UPDATE_COURIER_ORDER_STATUS = gql`
  ${updateCourierOrderStatus}
`
const DISPATCH_TO_RIDERS = gql`
  ${dispatchCourierOrderToRiders}
`

const SEARCH_DEBOUNCE_MS = 400
const TABLE_MIN_WIDTH = 2000

// Ops walks a courier order through these; the colours are reused by the
// filter chip and the per-row picker so one status always looks the same.
const COURIER_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: '#B45309', bg: '#FEF3C7' },
  { value: 'DISPATCHED', label: 'Dispatched', color: '#1D4ED8', bg: '#DBEAFE' },
  { value: 'IN_TRANSIT', label: 'In transit', color: '#6D28D9', bg: '#EDE9FE' },
  { value: 'DELIVERED', label: 'Delivered', color: '#047857', bg: '#D1FAE5' },
  { value: 'CANCELLED', label: 'Cancelled', color: '#B91C1C', bg: '#FEE2E2' }
]
const statusMeta = value =>
  COURIER_STATUSES.find(s => s.value === value) || COURIER_STATUSES[0]

const Courier = () => {
  const { t } = useTranslation()
  const globalClasses = useGlobalStyles()

  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [courierStatus, setCourierStatus] = useState('')
  const [editRow, setEditRow] = useState(null)
  const [carrier, setCarrier] = useState('')
  const [tracking, setTracking] = useState('')
  const [eta, setEta] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchQuery.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data, error, loading, previousData, refetch } = useQuery(
    GET_COURIER_ORDERS,
    {
      variables: {
        page,
        rows: limit,
        search,
        startDate: startDate || null,
        endDate: endDate || null,
        courierStatus: courierStatus || null
      },
      fetchPolicy: 'cache-and-network',
      // background polls must not flip `loading` and flash the table spinner
      notifyOnNetworkStatusChange: false,
      errorPolicy: 'all',
      pollInterval: 15000
    }
  )

  const [mutate, { loading: saving }] = useMutation(
    UPDATE_COURIER_ORDER_STATUS,
    {
      onCompleted: () => {
        setEditRow(null)
        refetch()
      }
    }
  )
  const [
    dispatchToRiders,
    { loading: dispatching }
  ] = useMutation(DISPATCH_TO_RIDERS, { onCompleted: () => refetch() })

  const orders =
    data?.courierOrdersPaginated?.orders ??
    previousData?.courierOrdersPaginated?.orders ??
    []
  const totalCount =
    data?.courierOrdersPaginated?.totalCount ??
    previousData?.courierOrdersPaginated?.totalCount ??
    0

  const changeStatus = (row, nextStatus) => {
    mutate({
      variables: {
        _id: row._id,
        courierStatus: nextStatus,
        courierName: row.courierName || null,
        courierTrackingNumber: row.courierTrackingNumber || null,
        courierEstimatedDelivery: row.courierEstimatedDelivery || null
      }
    })
  }

  const openEdit = row => {
    setEditRow(row)
    setCarrier(row.courierName || '')
    setTracking(row.courierTrackingNumber || '')
    setEta(toDateInput(row.courierEstimatedDelivery))
  }

  const saveEdit = () => {
    if (!editRow) return
    mutate({
      variables: {
        _id: editRow._id,
        courierStatus: editRow.courierStatus || 'PENDING',
        courierName: carrier || null,
        courierTrackingNumber: tracking || null,
        // Noon UTC so the picked calendar day survives any viewer's timezone.
        courierEstimatedDelivery: eta ? `${eta}T12:00:00.000Z` : null
      }
    })
  }

  const toDateInput = v => {
    if (!v) return ''
    const d = new Date(Number.isNaN(Number(v)) ? v : Number(v))
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
  }

  const val = v => (v === null || v === undefined ? '' : String(v))
  const fmtDate = v => {
    if (!v) return '—'
    const d = new Date(Number.isNaN(Number(v)) ? v : Number(v))
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const muted = { color: '#6B7280', fontSize: 12 }

  const columns = [
    {
      name: t('OrderID'),
      minWidth: '130px',
      selector: row => row.orderId,
      sortable: true,
      cell: row => (
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
          {val(row.orderId)}
        </Typography>
      )
    },
    {
      name: t('CreatedAt'),
      minWidth: '175px',
      selector: row => row.createdAt,
      cell: row => (
        <span style={{ fontSize: 13 }}>{fmtDate(row.createdAt)}</span>
      )
    },
    {
      name: t('RestaurantCol'),
      minWidth: '160px',
      wrap: true,
      selector: row => row.restaurant?.name || '',
      cell: row => (
        <span style={{ fontSize: 13 }}>{val(row.restaurant?.name) || '—'}</span>
      )
    },
    {
      name: t('Customer'),
      minWidth: '180px',
      wrap: true,
      selector: row => row.user?.name || '',
      cell: row => (
        <Box sx={{ py: 1 }}>
          <div style={{ fontSize: 13 }}>{val(row.user?.name) || '—'}</div>
          {row.user?.phone ? (
            <div style={muted}>{val(row.user.phone)}</div>
          ) : null}
        </Box>
      )
    },
    {
      name: t('DeliveryAddress'),
      minWidth: '230px',
      wrap: true,
      selector: row => row.deliveryAddress?.deliveryAddress || '',
      cell: row => (
        <Box sx={{ py: 1 }}>
          <div style={{ fontSize: 13 }}>
            {val(row.deliveryAddress?.deliveryAddress) || '—'}
          </div>
          {row.deliveryCountry ? (
            <span
              style={{
                ...muted,
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                padding: '1px 6px',
                display: 'inline-block',
                marginTop: 2
              }}>
              {row.deliveryCountry}
            </span>
          ) : null}
        </Box>
      )
    },
    {
      name: t('Amount'),
      minWidth: '100px',
      right: true,
      selector: row => row.orderAmount,
      cell: row => (
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {val(row.orderAmount)}
        </span>
      )
    },
    {
      name: t('CourierStatus'),
      minWidth: '175px',
      cell: row => {
        const meta = statusMeta(row.courierStatus)
        return (
          <Select
            size="small"
            value={meta.value}
            disabled={saving}
            onChange={e => changeStatus(row, e.target.value)}
            sx={{
              minWidth: 155,
              fontSize: 13,
              fontWeight: 600,
              color: meta.color,
              backgroundColor: meta.bg,
              borderRadius: '999px',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-icon': { color: meta.color },
              '& .MuiSelect-select': { py: 0.75 }
            }}>
            {COURIER_STATUSES.map(s => (
              <MenuItem
                key={s.value}
                value={s.value}
                sx={{ color: '#111827', fontSize: 13 }}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        )
      }
    },
    {
      name: t('Carrier'),
      minWidth: '130px',
      wrap: true,
      selector: row => row.courierName || '',
      cell: row => (
        <span style={{ fontSize: 13 }}>{val(row.courierName) || '—'}</span>
      )
    },
    {
      name: t('TrackingNumber'),
      minWidth: '160px',
      wrap: true,
      selector: row => row.courierTrackingNumber || '',
      cell: row => (
        <span style={{ fontSize: 13 }}>
          {val(row.courierTrackingNumber) || '—'}
        </span>
      )
    },
    {
      name: t('LastUpdated'),
      minWidth: '175px',
      selector: row => row.courierUpdatedAt,
      cell: row => (
        <span style={{ fontSize: 13 }}>{fmtDate(row.courierUpdatedAt)}</span>
      )
    },
    {
      name: t('Actions'),
      minWidth: '260px',
      cell: row => (
        <Box sx={{ display: 'flex', gap: 1, py: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openEdit(row)}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              borderColor: '#D1D5DB',
              color: '#374151'
            }}>
            {t('Edit')}
          </Button>
          {row.rider ? (
            <span style={muted}>{row.rider.name}</span>
          ) : (
            <Tooltip title={t('SendToRidersHelp')}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={dispatching}
                  onClick={() =>
                    dispatchToRiders({ variables: { _id: row._id } })
                  }
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    backgroundColor: '#F47627',
                    '&:hover': { backgroundColor: '#DC5F13' }
                  }}>
                  {t('SendToRiders')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )
    }
  ]

  const filterSx = {
    minWidth: 180,
    backgroundColor: '#fff',
    borderRadius: '999px',
    fontSize: 14,
    color: '#111827',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' }
  }

  const dialogFieldSx = {
    '& .MuiInputBase-input': { color: '#111827', fontSize: 14 },
    '& .MuiInputLabel-root': { color: '#6B7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#F47627' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#F47627'
    }
  }

  return (
    <>
      <Header />
      <Box className={globalClasses.flexRow} mb={2}></Box>
      <Container className={globalClasses.flex} fluid>
        {error && (
          <Box sx={{ color: '#B91C1C', mb: 1, px: 1, fontSize: 14 }}>
            {`Error: ${error.message}`}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            width: '100%',
            pb: 2
          }}>
          <Select
            size="small"
            displayEmpty
            value={courierStatus}
            onChange={e => {
              setCourierStatus(e.target.value)
              setPage(1)
            }}
            sx={filterSx}
            renderValue={v => (v ? statusMeta(v).label : t('AllStatuses'))}>
            <MenuItem value="" sx={{ color: '#111827', fontSize: 13 }}>
              {t('AllStatuses')}
            </MenuItem>
            {COURIER_STATUSES.map(s => (
              <MenuItem
                key={s.value}
                value={s.value}
                sx={{ color: '#111827', fontSize: 13 }}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChange={(s, e) => {
              setStartDate(s)
              setEndDate(e)
              setPage(1)
            }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <SearchBar
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('SearchOrderOrVoucher')}
          />
        </Box>

        {/* The table is wider than any screen, so it scrolls horizontally as a
            whole rather than squeezing every column. */}
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 1 }}>
          <Box sx={{ minWidth: loading ? '100%' : TABLE_MIN_WIDTH }}>
            <DataTable
              title={<TableHeader title={t('Courier')} />}
              keyField="_id"
              columns={columns}
              data={orders}
              responsive={false}
              progressPending={loading && orders.length === 0}
              progressComponent={<CustomLoader />}
              noDataComponent={
                <Box
                  sx={{
                    p: 5,
                    color: '#6B7280',
                    fontSize: 14,
                    minWidth: '1000px'
                  }}>
                  {t('NoCourierOrders')}
                </Box>
              }
              highlightOnHover
              pagination
              paginationServer
              paginationDefaultPage={page}
              paginationPerPage={limit}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              onChangeRowsPerPage={perPage => {
                setLimit(perPage)
                setPage(1)
              }}
              paginationTotalRows={totalCount}
              onChangePage={setPage}
              customStyles={customStyles}
            />
          </Box>
        </Box>
      </Container>

      <Dialog
        open={!!editRow}
        onClose={() => setEditRow(null)}
        fullWidth
        maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: '#374151' }}>
          {t('CourierDetails')}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '8px !important'
          }}>
          <TextField
            label={t('Carrier')}
            value={carrier}
            onChange={e => setCarrier(e.target.value)}
            size="small"
            fullWidth
            sx={dialogFieldSx}
          />
          <TextField
            label={t('TrackingNumber')}
            value={tracking}
            onChange={e => setTracking(e.target.value)}
            size="small"
            fullWidth
            sx={dialogFieldSx}
          />
          <TextField
            label={t('EstimatedDelivery')}
            type="date"
            value={eta}
            onChange={e => setEta(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().slice(0, 10) }}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditRow(null)}
            sx={{ textTransform: 'none', color: '#374151' }}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={saveEdit}
            disabled={saving}
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              backgroundColor: '#F47627',
              '&:hover': { backgroundColor: '#DC5F13' }
            }}>
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default withTranslation()(Courier)
